function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import Chart from "../../components/chart";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import { hasTenantFeature } from "../../functions/tenant-features";
import { fetchSales, titleCase, resolveBusinessModel } from "../sales/territory-common";
import { lapseKey } from "../sales/sales-api";
import { canViewQuotes } from "../sales/sales-quotes-api";
import { getCustomer, getCustomerNotes, addCustomerNote } from "../people/api";
import CustomerModal from "./customer-modal";
import { g, n, count, money, shortDate, monthYear, monthLabel, plural, StageChip, HealthChip, nounsFor } from "./index";

/*
 * Customer page (People redesign 2026-10-09, board "4 · A customer"): /Customers/account/{id}.
 *
 * Data: GET /v1/people/customers/{id} (account, stats, salesByMonth, orders, people, topItems, lapse)
 * and /notes. When that endpoint is not deployed yet (getCustomer returns null) the page builds the
 * same layout from GET /v1/lifecycle/customers/{id} plus /sales/totals/this_year?customer=.
 * Edit uses the existing CustomerModal, which wants the lifecycle record, so that record loads on demand.
 */

const CHANNEL_LABEL = {
  b2b: "Business",
  b2c: "Consumer"
};

/** 28 to "4 weeks", 60 to "2 months", 5 to "5 days". */
function cadence(days) {
  const d = Math.round(n(days));
  if (d <= 0) return "";
  if (d < 14) return d === 1 ? "day" : `${d} days`;
  if (d < 56) {
    const w = Math.round(d / 7);
    return w === 1 ? "week" : `${w} weeks`;
  }
  const m = Math.round(d / 30);
  return m === 1 ? "month" : `${m} months`;
}
function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}
const firstName = name => String(name || "").trim().split(/\s+/)[0] || "";

/** Build the page model from the new endpoint. */
function fromPeople(res) {
  const a = res.account || {};
  const stats = res.stats || {};
  return {
    id: String(a.id != null ? a.id : ""),
    name: a.name || "",
    customerNumber: a.customerNumber || "",
    channel: String(a.channel || "").toLowerCase(),
    city: a.city || "",
    state: a.state || "",
    since: a.since || null,
    stage: String(a.stage || "").toLowerCase(),
    apiHealth: a.health !== undefined ? a.health : undefined,
    email: a.email || "",
    phone: a.phone || "",
    stats: {
      salesYtd: stats.salesYtd != null ? n(stats.salesYtd) : null,
      orders: stats.orders != null ? n(stats.orders) : null,
      avgOrder: stats.avgOrder != null ? n(stats.avgOrder) : n(stats.orders) > 0 ? n(stats.salesYtd) / n(stats.orders) : null,
      lastOrder: stats.lastOrder || null,
      visits: stats.visits != null ? n(stats.visits) : null,
      pageViews: stats.pageViews != null ? n(stats.pageViews) : null
    },
    salesByMonth: (res.salesByMonth || []).map(m => ({
      month: m.month,
      amount: n(m.amount)
    })),
    orders: res.orders || [],
    people: (res.people || []).map(p => ({
      ...p,
      id: p.id != null ? String(p.id) : ""
    })),
    topItems: res.topItems || [],
    lapse: res.lapse || null,
    legacy: false
  };
}

/** Build the same model from the lifecycle record (fallback while the new endpoint is pending). */
function fromLifecycle(data, sales) {
  const c = data && data.customer || {};
  const k = lapseKey(g(c, "lapseRisk"));
  const days = g(c, "daysSinceOrder");
  const cad = n(g(c, "expectedCadenceDays"));
  const lapse = k && k !== "healthy" && cad > 0 && days != null ? {
    usualDays: cad,
    daysSinceLast: n(days),
    daysLate: Math.max(0, n(days) - cad),
    basedOnOrders: null,
    months: null
  } : null;
  const orders = sales ? n(sales.orders) : null;
  return {
    id: String(g(c, "id") != null ? g(c, "id") : ""),
    name: g(c, "name") && !String(g(c, "name")).includes("@") ? titleCase(g(c, "name")) : "",
    customerNumber: /^P_/i.test(g(c, "customerNumber") || "") ? "" : g(c, "customerNumber") || "",
    channel: String(g(c, "lifecycleChannel") || "").toLowerCase(),
    city: g(c, "shipToCity") || g(c, "billToCity") || "",
    state: g(c, "shipToRegion") || g(c, "billToRegion") || "",
    since: g(c, "createdDate") || g(c, "firstOrderDate") || g(c, "stageEnteredDate") || null,
    stage: String(g(c, "lifecycleStage") || "").toLowerCase(),
    email: g(c, "email") || "",
    phone: g(c, "phone") || "",
    stats: {
      salesYtd: sales ? n(sales.totalAmount) : null,
      orders,
      avgOrder: orders ? n(sales.totalAmount) / orders : null,
      lastOrder: g(c, "lastOrderDate") || null
    },
    salesByMonth: [],
    orders: [],
    people: (data && data.contacts || []).map(p => ({
      id: g(p, "personId") != null ? String(g(p, "personId")) : "",
      name: g(p, "name") || g(p, "email") || "",
      role: g(p, "isPrimary") ? "Main contact" : g(p, "role") || "",
      lastSeen: g(p, "lastSeen") || null
    })),
    topItems: [],
    lapse,
    legacyHealth: k ? k === "healthy" ? "healthy" : "attention" : null,
    legacy: true
  };
}
function healthOf(m) {
  // The API's health uses the same rule as the Customers list, so the two always agree.
  if (m.apiHealth !== undefined) return m.apiHealth;
  if (m.legacy && m.legacyHealth) return m.legacyHealth;
  if (m.lapse && n(m.lapse.daysLate) > 0) return "attention";
  const orders = n(m.stats.orders);
  const since = m.since ? new Date(m.since) : null;
  const recent = since && !isNaN(since.getTime()) && Date.now() - since.getTime() < 60 * 86400000;
  if (!orders || recent) return m.stage ? "new" : null;
  return "healthy";
}
class CustomerAccountDetailComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onDocDown", e => {
      if (this.state.menuOpen && this._menu && !this._menu.contains(e.target)) this.setState({
        menuOpen: false
      });
    });
    // Edit reuses CustomerModal, which wants the lifecycle record.
    _defineProperty(this, "openEdit", async () => {
      if (this.state.lifecycle) {
        this.setState({
          modalOpen: true
        });
        return;
      }
      try {
        const data = await fetchSales(`/lifecycle/customers/${encodeURIComponent(this.id())}`);
        if (this._alive) this.setState({
          lifecycle: data,
          modalOpen: true
        });
      } catch (e) {/* record missing: nothing to edit */}
    });
    _defineProperty(this, "onSaved", () => {
      this.setState({
        modalOpen: false,
        model: null,
        lifecycle: null
      }, () => this.load());
    });
    _defineProperty(this, "saveNote", async () => {
      const text = this.state.noteText.trim();
      if (!text || this.state.noteSaving) return;
      this.setState({
        noteSaving: true,
        noteMsg: ""
      });
      try {
        const note = await addCustomerNote(this.id(), text);
        if (!this._alive) return;
        const item = note && typeof note === "object" ? note : {
          text,
          at: new Date().toISOString()
        };
        this.setState({
          notes: [item, ...(this.state.notes || [])],
          noteText: "",
          noteSaving: false
        });
      } catch (e) {
        if (!this._alive) return;
        const soft = e && (e.status === 404 || e.status === 405 || e.status === 501);
        this.setState({
          noteSaving: false,
          noteMsg: soft ? "Notes are not set up for this workspace yet" : "That note did not save. Try again."
        });
      }
    });
    this.state = {
      model: null,
      error: false,
      businessModel: null,
      lifecycle: null,
      notes: null,
      noteText: "",
      noteSaving: false,
      noteMsg: "",
      modalOpen: false,
      menuOpen: false
    };
    this._alive = true;
  }
  id() {
    return String(this.props.params.id);
  }
  async componentDidMount() {
    resolveBusinessModel().then(bm => {
      if (this._alive) this.setState({
        businessModel: bm
      });
    }).catch(() => {});
    document.addEventListener("mousedown", this.onDocDown);
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    document.removeEventListener("mousedown", this.onDocDown);
  }
  componentDidUpdate(prev) {
    if (String(prev.params.id) !== this.id()) {
      this.setState({
        model: null,
        notes: null,
        lifecycle: null
      }, () => this.load());
    }
  }
  async load() {
    const id = this.id();
    try {
      const res = await getCustomer(id);
      if (!this._alive || id !== this.id()) return;
      if (res) this.setState({
        model: fromPeople(res)
      });else await this.loadLegacy(id);
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
      return;
    }
    getCustomerNotes(id).then(r => {
      if (this._alive && id === this.id()) this.setState({
        notes: r ? r.items || [] : []
      });
    }).catch(() => {
      if (this._alive) this.setState({
        notes: []
      });
    });
  }
  async loadLegacy(id) {
    const data = await fetchSales(`/lifecycle/customers/${encodeURIComponent(id)}`);
    if (!this._alive) return;
    const cust = data && data.customer || {};
    const number = g(cust, "customerNumber");
    let sales = null;
    if (hasTenantFeature("salesTransactions") && number && !/^P_/i.test(number)) {
      sales = await fetchSales(`/sales/totals/this_year?customer=${encodeURIComponent(number)}`).catch(() => null);
    }
    if (!this._alive) return;
    this.setState({
      lifecycle: data,
      model: fromLifecycle(data, sales)
    });
  }
  // Full sales history lives on the Territory drill for wholesale books with the B2B feeds.
  ordersRoute(m) {
    if (hasTenantFeature("territoryRep") && this.state.businessModel !== "retail" && m.customerNumber) {
      return `/Territory/this_year/customer/${encodeURIComponent(m.customerNumber)}`;
    }
    return null;
  }
  menuItems(m) {
    const items = [];
    if (hasTenantFeature("quotes") && canViewQuotes() && this.state.businessModel !== "retail") {
      items.push(["Quotes", `/Quotes?customerId=${encodeURIComponent(this.id())}&customer=${encodeURIComponent(m.name || "")}`]);
      items.push(["New quote", `/Quotes/new?customerId=${encodeURIComponent(this.id())}`]);
    }
    const r = this.ordersRoute(m);
    if (r) items.push(["Full sales detail", r]);
    return items;
  }
  renderAttention(m) {
    const l = m.lapse;
    if (!l) return null;
    const every = cadence(l.usualDays);
    const late = n(l.daysLate);
    const head = [every ? `Usually orders every ${every}.` : null, late > 0 ? `Last order was ${plural(late, "day", "days")} late.` : n(l.usualDays) > n(l.daysSinceLast) ? `Next order is due in ${plural(n(l.usualDays) - n(l.daysSinceLast), "day", "days")}.` : null].filter(Boolean).join(" ");
    const basis = n(l.basedOnOrders) > 0 ? `Based on ${plural(l.basedOnOrders, "order", "orders")}${n(l.months) > 0 ? ` over ${plural(l.months, "month", "months")}` : ""}.` : "";
    const first = m.people[0];
    const fname = first ? firstName(first.name) : "";
    // No single-person composer route exists yet: mail the account when we have an address, else open the person.
    let cta = null;
    if (m.email) cta = /*#__PURE__*/React.createElement("a", {
      className: "pcu-btn-primary",
      href: `mailto:${m.email}`
    }, fname ? `Email ${fname}` : "Email");else if (first && first.id) cta = /*#__PURE__*/React.createElement(Link, {
      className: "pcu-btn-primary",
      to: `/Contacts/Detail/${encodeURIComponent(first.id)}`
    }, `Open ${fname || "contact"}`);
    return /*#__PURE__*/React.createElement("section", {
      className: `pcu-card pcu-attn${late > 0 ? " late" : ""}`
    }, /*#__PURE__*/React.createElement("div", {
      className: "pcu-attn-text"
    }, head ? /*#__PURE__*/React.createElement("b", null, head) : null, basis ? /*#__PURE__*/React.createElement("span", null, basis) : null), cta);
  }
  renderChart(m) {
    const fullMonth = k => {
      const d = new Date(`${k}-01T00:00:00`);
      return isNaN(d) ? k : d.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric"
      });
    };
    const months = m.salesByMonth.slice(-6);
    if (!months.length) return null;
    return /*#__PURE__*/React.createElement("section", {
      className: "pcu-card pcu-chart"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pcu-cardhead"
    }, /*#__PURE__*/React.createElement("b", null, "Sales by month"), m.stats.salesYtd != null ? /*#__PURE__*/React.createElement("span", {
      className: "pcu-muted"
    }, money(m.stats.salesYtd), " this year") : null), /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "220px",
      chartType: "ColumnChart",
      data: [["Month", "Sales"], ...months.map(x => [fullMonth(x.month), x.amount])],
      options: {
        colors: ["#ff6358"],
        legend: {
          position: "none"
        },
        vAxis: {
          format: "short"
        },
        tooltip: {
          format: "currency"
        }
      }
    }));
  }
  renderOrders(m) {
    if (!m.orders.length) {
      if (m.legacy) return null;
      return /*#__PURE__*/React.createElement("section", {
        className: "pcu-card"
      }, /*#__PURE__*/React.createElement("div", {
        className: "pcu-cardhead"
      }, /*#__PURE__*/React.createElement("b", null, "Orders")), /*#__PURE__*/React.createElement("div", {
        className: "pcu-empty"
      }, "No orders yet."));
    }
    const route = this.ordersRoute(m);
    const total = n(m.stats.orders);
    return /*#__PURE__*/React.createElement("section", {
      className: "pcu-card pcu-orders"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pcu-cardhead"
    }, /*#__PURE__*/React.createElement("b", null, "Orders"), route && total > 0 ? /*#__PURE__*/React.createElement(Link, {
      to: route
    }, "All ", plural(total, "order", "orders")) : null), m.orders.map((o, i) => /*#__PURE__*/React.createElement("div", {
      key: `${o.number || ""}-${i}`,
      className: "pcu-order"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pcu-muted"
    }, shortDate(o.date)), /*#__PURE__*/React.createElement("span", {
      className: "pcu-ellipsis",
      title: o.number ? String(o.number) : undefined
    }, o.number ? String(o.number).startsWith("#") ? o.number : `#${o.number}` : ""), /*#__PURE__*/React.createElement("span", {
      className: "pcu-muted pcu-ellipsis"
    }, o.items || ""), /*#__PURE__*/React.createElement("span", {
      className: "pcu-num pcu-strong"
    }, money(o.amount)), /*#__PURE__*/React.createElement("span", {
      className: "pcu-num pcu-muted"
    }, o.status ? titleCase(o.status) : ""))));
  }
  renderStats(m) {
    const s = m.stats;
    const tiles = [s.salesYtd != null ? [money(s.salesYtd), "sales this year"] : null, s.orders != null ? [count(s.orders), "orders"] : null, s.avgOrder != null && n(s.orders) > 0 ? [money(s.avgOrder), "average order"] : null, s.lastOrder ? [shortDate(s.lastOrder), "last order"] : null, s.visits != null ? [count(s.visits), "site visits"] : null, s.pageViews != null ? [count(s.pageViews), "page views"] : null].filter(Boolean);
    if (!tiles.length) return null;
    return /*#__PURE__*/React.createElement("section", {
      className: "pcu-card pcu-stats"
    }, tiles.map(([v, l]) => /*#__PURE__*/React.createElement("span", {
      key: l,
      className: "pcu-stat"
    }, /*#__PURE__*/React.createElement("b", null, v), /*#__PURE__*/React.createElement("span", null, l))));
  }
  renderPeople(m) {
    return /*#__PURE__*/React.createElement("section", {
      className: "pcu-card pcu-people"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pcu-cardtitle"
    }, "People"), m.people.length ? m.people.map((p, i) => /*#__PURE__*/React.createElement("div", {
      key: p.id || i,
      className: "pcu-person"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pcu-avatar"
    }, initials(p.name)), /*#__PURE__*/React.createElement("div", {
      className: "pcu-person-who"
    }, p.id ? /*#__PURE__*/React.createElement(Link, {
      to: `/Contacts/Detail/${encodeURIComponent(p.id)}`
    }, p.name || "Unnamed contact") : /*#__PURE__*/React.createElement("b", null, p.name || "Unnamed contact"), p.role ? /*#__PURE__*/React.createElement("span", {
      className: "pcu-sub"
    }, p.role) : null), /*#__PURE__*/React.createElement("span", {
      className: "pcu-sub"
    }, p.lastSeen ? `Seen ${shortDate(p.lastSeen)}` : "Not seen yet"))) : /*#__PURE__*/React.createElement("div", {
      className: "pcu-empty pcu-empty-sm"
    }, "No people linked yet. They attach through your customer feed and site sign-ins."));
  }
  renderAccount(m, nouns) {
    const place = [m.city ? titleCase(m.city) : "", m.state ? String(m.state).toUpperCase() : ""].filter(Boolean).join(", ");
    const kv = [[`${nouns.word} #`, m.customerNumber], ["Channel", CHANNEL_LABEL[m.channel] || (m.channel ? titleCase(m.channel) : "")], ["Ships to", place], ["Since", monthYear(m.since)], ["Top items", m.topItems.slice(0, 3).join(", ")], ["Email", m.email], ["Phone", m.phone]].filter(([, v]) => v);
    if (!kv.length) return null;
    return /*#__PURE__*/React.createElement("section", {
      className: "pcu-card pcu-account"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pcu-cardtitle"
    }, "Account"), kv.map(([k, v]) => /*#__PURE__*/React.createElement("div", {
      key: k,
      className: "pcu-kv"
    }, /*#__PURE__*/React.createElement("span", null, k), /*#__PURE__*/React.createElement("span", null, v))));
  }
  renderNotes() {
    const {
      notes,
      noteText,
      noteSaving,
      noteMsg
    } = this.state;
    return /*#__PURE__*/React.createElement("section", {
      className: "pcu-card pcu-notes"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pcu-cardtitle"
    }, "Notes"), (notes || []).map((x, i) => /*#__PURE__*/React.createElement("div", {
      key: x.id != null ? String(x.id) : i,
      className: "pcu-note"
    }, /*#__PURE__*/React.createElement("p", null, x.text), /*#__PURE__*/React.createElement("span", {
      className: "pcu-sub"
    }, [x.author, shortDate(x.at)].filter(Boolean).join(" · ")))), /*#__PURE__*/React.createElement("textarea", {
      className: "pcu-note-input",
      rows: 3,
      value: noteText,
      placeholder: "Add a note for your team...",
      onChange: e => this.setState({
        noteText: e.target.value,
        noteMsg: ""
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "pcu-note-foot"
    }, noteMsg ? /*#__PURE__*/React.createElement("span", {
      className: "pcu-sub"
    }, noteMsg) : /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !noteText.trim() || noteSaving,
      onClick: this.saveNote
    }, noteSaving ? "Saving..." : "Save")));
  }
  render() {
    const {
      model: m,
      error,
      menuOpen
    } = this.state;
    const nouns = nounsFor(this.state.businessModel);
    if (error) return /*#__PURE__*/React.createElement(Error, {
      message: `Data for this ${nouns.one} does not exist.`
    });
    if (!m) return /*#__PURE__*/React.createElement(Loader, null);
    const health = healthOf(m);
    const place = [m.city ? titleCase(m.city) : "", m.state ? String(m.state).toUpperCase() : ""].filter(Boolean).join(", ");
    const sinceText = m.since ? m.stage === "customer" ? `${nouns.one} since ${monthYear(m.since)}` : `added ${monthYear(m.since)}` : "";
    const line = [place, sinceText, m.people.length ? plural(m.people.length, "person", "people") : ""].filter(Boolean).join(" · ");
    const menu = this.menuItems(m);
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view pcu-view pcu-detail ec-form"
    }, /*#__PURE__*/React.createElement("header", {
      className: "pcu-subhead"
    }, /*#__PURE__*/React.createElement(Link, {
      to: "/Customers",
      className: "pcu-back",
      "aria-label": "Back"
    }, /*#__PURE__*/React.createElement("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M15 18l-6-6 6-6"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "pcu-subhead-text"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pcu-subhead-title"
    }, /*#__PURE__*/React.createElement("h1", null, m.name || m.email || m.customerNumber || `Unnamed ${nouns.one}`), /*#__PURE__*/React.createElement(StageChip, {
      stage: m.stage,
      customerWord: nouns.word
    }), /*#__PURE__*/React.createElement(HealthChip, {
      health: health
    })), line ? /*#__PURE__*/React.createElement("span", {
      className: "pcu-sub"
    }, line) : null), /*#__PURE__*/React.createElement("span", {
      className: "pcu-flex"
    }), /*#__PURE__*/React.createElement(Button, {
      onClick: this.openEdit
    }, "Edit"), menu.length ? /*#__PURE__*/React.createElement("span", {
      className: "pcu-menu-wrap",
      ref: el => {
        this._menu = el;
      }
    }, /*#__PURE__*/React.createElement(Button, {
      "aria-label": "More actions",
      "aria-expanded": menuOpen,
      onClick: () => this.setState({
        menuOpen: !menuOpen
      })
    }, "\xB7\xB7\xB7"), menuOpen ? /*#__PURE__*/React.createElement("div", {
      className: "pcu-menu",
      role: "menu"
    }, menu.map(([label, to]) => /*#__PURE__*/React.createElement("button", {
      key: label,
      type: "button",
      role: "menuitem",
      onClick: () => {
        this.setState({
          menuOpen: false
        });
        this.props.history.push(to);
      }
    }, label))) : null) : null), /*#__PURE__*/React.createElement("div", {
      className: "pcu-cols"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pcu-main"
    }, this.renderAttention(m), this.renderChart(m), this.renderOrders(m), m.legacy && n(m.stats.orders) === 0 && m.stage !== "customer" ? /*#__PURE__*/React.createElement("section", {
      className: "pcu-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pcu-empty"
    }, "No orders yet. The first sale makes this account a ", nouns.one, ".")) : null), /*#__PURE__*/React.createElement("div", {
      className: "pcu-side"
    }, this.renderStats(m), this.renderPeople(m), this.renderAccount(m, nouns), this.renderNotes())), this.state.modalOpen && this.state.lifecycle && this.state.lifecycle.customer && /*#__PURE__*/React.createElement(CustomerModal, {
      customer: this.state.lifecycle.customer,
      dealers: this.state.businessModel != null && this.state.businessModel !== "retail" && this.state.businessModel !== "both",
      onClose: () => this.setState({
        modalOpen: false
      }),
      onSaved: this.onSaved
    }));
  }
}
export default withRouter(CustomerAccountDetailComponent);